// src/pages/leaderManagement/leaderDetails/leaderDetails.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Button, DatePicker, Form, Input, Select, Spin } from "antd";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import { initialRoles } from "../../enabledRoles/enabledRoles";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import "./leaderDetails.css";

function LeaderDetails() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { userId } = useParams();
  const [leader, setLeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleAssignLoading, setRoleAssignLoading] = useState(false);

  // Available roles from roleEnable.js
  const availableRoles = initialRoles;

  // Activity status helper function
  const getLastActiveTime = (lastAdminLoginAt) => {
    if (!lastAdminLoginAt) return "Inactive";

    const now = new Date();
    const loginTime = new Date(lastAdminLoginAt);
    const diffInMinutes = Math.floor((now - loginTime) / (1000 * 60));

    if (diffInMinutes < 1) return "Active now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  useEffect(() => {
    if (!userId) return;

    let isMounted = true;

    const fetchLeader = async () => {
      setLoading(true);

      try {
        const token = await getAuthToken();

        const response = await axios.get(`${API_ENDPOINTS.USERS}?status=signedup`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!isMounted) return;

        const found = (response.data.users || []).find((u) => u.userId === userId);

        setLeader(found || null);

        if (found) {
          form.setFieldsValue({
            fullName: found.name || "",
            email: found.email || "",
            phoneNumber: found.phoneNumber || "",
            registrationDate: found.createdAt ? dayjs(found.createdAt) : null,
            lastLogin: found.lastAdminLoginAt ? dayjs(found.lastAdminLoginAt) : null,
            activityStatus: getLastActiveTime(found.lastAdminLoginAt),
            currentRole: found.role || "No Role Assigned",
            approvalStatus: found.isAdminApproved ? "Approved" : "Not Approved"
          });
        }
      } catch (err) {
        if (isMounted) {
          setLeader(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLeader();

    return () => {
      isMounted = false;
    };
  }, [userId, form]);

  // Role assignment function
  const handleRoleAssign = async (newGroupName) => {
    setRoleAssignLoading(true);

    try {
      const currentGroupName = leader.role;
      const token = await getAuthToken();
      // If selecting the same role, do nothing
      if (currentGroupName === newGroupName) {
        showNotification(`User already has "${newGroupName}" role`, "info");
        setRoleAssignLoading(false);
        return;
      }

      // Remove current role if exists
      if (currentGroupName && currentGroupName !== "") {
        await axios.post(
          API_ENDPOINTS.USER_ROLES,
          {
            action: "remove",
            username: userId,
            groupName: currentGroupName
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          }
        );
      }

      // Add new role if selected
      if (newGroupName && newGroupName !== "") {
        await axios.post(
          API_ENDPOINTS.USER_ROLES,
          {
            action: "add",
            username: userId,
            groupName: newGroupName
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          }
        );
      }

      // Success message based on operation
      let message = "";
      if (currentGroupName && newGroupName) {
        message = `Role changed from "${currentGroupName}" to "${newGroupName}" successfully!`;
      } else if (newGroupName) {
        message = `Role "${newGroupName}" assigned successfully!`;
      } else if (currentGroupName) {
        message = `Role "${currentGroupName}" removed successfully!`;
      } else {
        message = `Role updated successfully!`;
      }

      showNotification(message, "success");

      // Update local state
      setLeader((prev) => ({ ...prev, role: newGroupName }));
      form.setFieldValue("currentRole", newGroupName || "No Role Assigned");

      // Refresh leader data after a short delay
      setTimeout(async () => {
        const token = await getAuthToken();
        axios
          .get(`${API_ENDPOINTS.USERS}?status=signedup`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          .then((response) => {
            const found = response.data.users.find((u) => u.userId === userId);
            setLeader(found || null);
            if (found) {
              form.setFieldsValue({
                currentRole: found.role || "No Role Assigned",
                approvalStatus: found.isAdminApproved ? "Approved" : "Not Approved"
              });
            }
          })
          .catch((error) => {
            console.error("Error refreshing leader data:", error);
            showNotification("Failed to refresh leader data.", "error");
          });
      }, 1000);
    } catch (error) {
      console.error("Role assignment error:", error);
      showNotification("Failed to update role: " + (error.response?.data?.body || error.message), "error");
    } finally {
      setRoleAssignLoading(false);
    }
  };

  // Approve function
  const handleApprove = async () => {
    try {
      const token = await getAuthToken();
      await axios.put(
        `${API_ENDPOINTS.USERS}/${userId}`,
        { isAdminApproved: true },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      showNotification("User approved successfully!", "success");
      setLeader((prev) => ({ ...prev, isAdminApproved: true }));
      form.setFieldValue("approvalStatus", "Approved");
    } catch (error) {
      console.error("Error approving user:", error);
      showNotification("Failed to approve user. Please try again.", "error");
    }
  };

  return (
    <Layout
      navTitle="Leader Management"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="vld-form-container">
        <div className="vld-form-wrapper">
          {loading ? (
            <div style={{ textAlign: "center", padding: "32px" }}>
              <Spin size="large" style={{ marginBottom: "10px" }} />
              <p>Loading leader details...</p>
            </div>
          ) : !leader ? (
            <div style={{ textAlign: "center", padding: "32px" }}>No leader found.</div>
          ) : (
            <Form form={form} layout="vertical" className="vld-antd-form">
              <div className="vld-form-section">
                <h3 className="vld-section-title">Personal Information</h3>
                <div className="vld-form-row">
                  <Form.Item label="Full Name" name="fullName">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="Email" name="email">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="Phone Number" name="phoneNumber">
                    <Input disabled />
                  </Form.Item>
                </div>
              </div>

              <div className="vld-form-section">
                <h3 className="vld-section-title">Account Information</h3>
                <div className="vld-form-row">
                  <Form.Item label="Registration Date" name="registrationDate">
                    <DatePicker disabled style={{ width: "100%" }} format="DD-MMM-YYYY" />
                  </Form.Item>
                  <Form.Item label="Last Admin Login" name="lastLogin">
                    <DatePicker disabled style={{ width: "100%" }} format="YYYY-MM-DD" />
                  </Form.Item>
                  <Form.Item label="Activity Status" name="activityStatus">
                    <Input disabled />
                  </Form.Item>
                </div>
              </div>

              <div className="vld-form-section">
                <h3 className="vld-section-title">Role Management</h3>
                <div className="vld-form-row">
                  <Form.Item label="Current Role" name="currentRole">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="Assign New Role" name="assignRole">
                    <Select
                      value={leader.role || undefined}
                      onChange={(value) => handleRoleAssign(value)}
                      placeholder="Select role"
                      options={[
                        { value: "", label: "No Role" },
                        ...availableRoles.map((role) => ({
                          value: role.type,
                          label: role.type
                        }))
                      ]}
                      disabled={roleAssignLoading}
                      loading={roleAssignLoading}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="vld-form-section">
                <h3 className="vld-section-title">Approval Status</h3>
                <div className="vld-form-row">
                  <Form.Item label="Admin Approval Status" name="approvalStatus">
                    <Input disabled />
                  </Form.Item>
                </div>
                {leader && !leader.isAdminApproved && (
                  <div className="vld-form-row">
                    <Button onClick={handleApprove} style={{ marginTop: "10px" }}>
                      Approve User
                    </Button>
                  </div>
                )}
              </div>

              <div className="mbd-form-btn-group">
                <Button className="mbd-form-cancel-btn" onClick={() => navigate("/leaderList")}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default LeaderDetails;
